import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { BendingMachineService } from '../../bending_machine.service';
import { BendingMachine } from '../../../../_models/bending_machine';

@Component({
  selector: 'app-bending-machine-list',
  templateUrl: './bending-machine-list.component.html',
  styleUrls: ['../../../master-page.css']
})
export class BendingMachineListComponent implements OnInit {
  searchForm: FormGroup;
  editorForm: FormGroup;
  rows: BendingMachine[] = [];
  selected: BendingMachine = null;
  loading = false;
  saving = false;
  deleteModal = false;
  errorMessage = '';
  feedbackMessage = '';

  constructor(private formBuilder: FormBuilder, private bendingMachineService: BendingMachineService) {}

  ngOnInit(): void {
    this.searchForm = this.formBuilder.group({ f_name: [''], f_type: [''] });
    this.editorForm = this.formBuilder.group({
      id: [null], name: ['', Validators.required], cnc_type: ['Lombarda', Validators.required],
      speed_min_per_ml: [5, [Validators.required, Validators.min(0)]],
      load_unload_min: [0, [Validators.required, Validators.min(0)]],
      std_profile_discount: ['0,5', Validators.required],
      order: [1], active: [true]
    });
    this.loadRows();
  }

  loadRows(): void {
    this.loading = true;
    this.errorMessage = '';
    this.bendingMachineService.getBendingMachines(this.searchForm.value).subscribe(data => {
      this.loading = false;
      this.rows = Array.isArray(data) ? data.map(item => this.normalizeRow(item)) : [];
    });
  }

  clearFilters(): void { this.searchForm.reset({ f_name: '', f_type: '' }); this.loadRows(); }

  createNew(): void {
    this.selected = null;
    this.errorMessage = '';
    this.feedbackMessage = '';
    this.editorForm.reset({ id: null, name: '', cnc_type: 'Lombarda', speed_min_per_ml: 5, load_unload_min: 0, std_profile_discount: '0,5', order: this.nextOrder(), active: true });
  }

  editRow(rowIn: BendingMachine): void {
    const row = this.normalizeRow(rowIn);
    this.selected = rowIn;
    this.errorMessage = '';
    this.feedbackMessage = '';
    this.editorForm.reset({
      id: row.id, name: row.name, cnc_type: row.cnc_type,
      speed_min_per_ml: row.speed_min_per_ml, load_unload_min: row.load_unload_min,
      std_profile_discount: this.localized(row.std_profile_discount), order: row.order, active: row.active
    });
  }

  saveRow(): void {
    if (this.saving) return;
    if (this.editorForm.invalid) { this.errorMessage = 'Revisa el nombre, el tipo CNC y los tiempos de la plegadora.'; return; }
    const discountError = this.getDiscountValidationError(this.editorForm.value.std_profile_discount);
    if (discountError) { this.errorMessage = discountError; return; }
    const raw = this.editorForm.value;
    const payload = this.normalizeRow(Object.assign({}, this.selected || {}, raw, {
      std_profile_discount: this.toLocalizedNumber(raw.std_profile_discount, 0.5)
    }));
    this.saving = true;
    this.errorMessage = '';
    this.feedbackMessage = '';
    const request = payload.id ? this.bendingMachineService.updateBendingMachine(payload) : this.bendingMachineService.createBendingMachine(payload);
    request.subscribe(data => {
      this.saving = false;
      if (!data) { this.errorMessage = 'No se ha podido guardar la plegadora.'; return; }
      this.editRow(data);
      this.feedbackMessage = 'Plegadora guardada correctamente.';
      this.loadRows();
    });
  }

  askDelete(rowIn: BendingMachine): void { this.selected = rowIn; this.deleteModal = true; }

  confirmDelete(): void {
    if (!this.selected) return;
    this.bendingMachineService.deleteBendingMachine(this.selected).subscribe(result => {
      this.deleteModal = false;
      if (result === null || result === undefined) { this.errorMessage = 'No se ha podido eliminar la plegadora.'; return; }
      this.createNew();
      this.feedbackMessage = 'Plegadora eliminada correctamente.';
      this.loadRows();
    });
  }

  getDiscountValidationError(valueIn: any): string {
    if (valueIn === null || valueIn === undefined || valueIn.toString().trim() === '') return 'El descuento de perfil estándar es obligatorio.';
    const raw = valueIn.toString().trim();
    if (!/^[0-9]+([.,][0-9]{1,2})?$/.test(raw)) return 'El descuento debe indicarse en milímetros, por ejemplo 7,5.';
    const numeric = this.toLocalizedNumber(raw, NaN);
    return !Number.isFinite(numeric) || numeric <= 0 ? 'El descuento de perfil estándar debe ser mayor que cero.' : '';
  }

  private normalizeRow(rowIn: any): BendingMachine {
    const row = rowIn || {};
    return {
      id: row.id ? Number(row.id) : null, name: row.name ? row.name.toString() : '',
      cnc_type: row.cnc_type ? row.cnc_type.toString() : 'Lombarda',
      speed_min_per_ml: this.toNumber(row.speed_min_per_ml, 5), load_unload_min: this.toNumber(row.load_unload_min, 0),
      std_profile_discount: this.toLocalizedNumber(row.std_profile_discount, 0.5), enviroment: row.enviroment ? row.enviroment.toString() : '',
      order: Math.round(this.toNumber(row.order, 0)), active: row.active === undefined || row.active === null ? true : !!row.active
    };
  }

  private nextOrder(): number { return this.rows.reduce((max, item) => Math.max(max, Number(item.order || 0)), 0) + 1; }
  private toNumber(valueIn: any, fallbackIn: number): number { const num = Number(valueIn); return isNaN(num) ? fallbackIn : num; }
  private toLocalizedNumber(valueIn: any, fallbackIn: number): number { const num = Number(valueIn === null || valueIn === undefined ? valueIn : valueIn.toString().trim().replace(',', '.')); return isNaN(num) ? fallbackIn : num; }
  private localized(valueIn: any): string { return this.toLocalizedNumber(valueIn, 0).toString().replace('.', ','); }
}
